/**
 * React hooks for managing lesson prompts
 */

import { useState, useEffect, useCallback } from 'react';
import { lessonPromptService } from '@/services/lessonPromptService';
import {
  LessonPromptData,
  LessonPromptInput,
  LessonWithPromptStatus,
  PromptHistoryEntry,
} from '@/types/lessonPrompt';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for fetching all lessons with their prompt status
 * Used in admin list view
 */
export function useLessonPromptList() {
  const [lessons, setLessons] = useState<LessonWithPromptStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchLessons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await lessonPromptService.getLessonsWithPromptStatus();
      setLessons(data);
    } catch (err) {
      console.error('[useLessonPromptList] Error:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  return { lessons, loading, error, refetch: fetchLessons };
}

/**
 * Hook for fetching and managing a single lesson prompt
 * Used in prompt editor
 */
export function useLessonPrompt(lessonId: string | null) {
  const [prompt, setPrompt] = useState<LessonPromptData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchPrompt = useCallback(async () => {
    if (!lessonId) {
      setPrompt(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await lessonPromptService.getPromptByLessonId(lessonId);
      setPrompt(data);
    } catch (err) {
      console.error('[useLessonPrompt] Error fetching:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    fetchPrompt();
  }, [fetchPrompt]);

  const savePrompt = useCallback(
    async (input: LessonPromptInput) => {
      if (!user?.id) {
        toast({
          title: 'Not authenticated',
          description: 'You must be logged in to save prompts.',
          variant: 'destructive',
        });
        return null;
      }

      setSaving(true);
      setError(null);
      try {
        const saved = await lessonPromptService.savePrompt(input, user.id);
        setPrompt(saved);
        toast({
          title: 'Prompt saved',
          description: `Successfully saved prompt for ${input.lessonId}`,
        });
        return saved;
      } catch (err) {
        console.error('[useLessonPrompt] Error saving:', err);
        setError(err as Error);
        toast({
          title: 'Error saving prompt',
          description: (err as Error).message,
          variant: 'destructive',
        });
        return null;
      } finally {
        setSaving(false);
      }
    },
    [user?.id, toast]
  );

  const deletePrompt = useCallback(async () => {
    if (!lessonId) return;

    setSaving(true);
    setError(null);
    try {
      await lessonPromptService.deletePrompt(lessonId);
      setPrompt(null);
      toast({
        title: 'Prompt deleted',
        description: 'Lesson will now use file-based defaults.',
      });
    } catch (err) {
      console.error('[useLessonPrompt] Error deleting:', err);
      setError(err as Error);
      toast({
        title: 'Error deleting prompt',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [lessonId, toast]);

  return {
    prompt,
    loading,
    saving,
    error,
    refetch: fetchPrompt,
    savePrompt,
    deletePrompt,
  };
}

/**
 * Hook for fetching prompt version history
 */
export function usePromptHistory(lessonId: string | null) {
  const [history, setHistory] = useState<PromptHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchHistory = useCallback(async () => {
    if (!lessonId) {
      setHistory([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await lessonPromptService.getPromptHistory(lessonId);
      setHistory(data);
    } catch (err) {
      console.error('[usePromptHistory] Error:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const restoreFromHistory = useCallback(
    async (historyId: string) => {
      if (!user?.id) {
        toast({
          title: 'Not authenticated',
          description: 'You must be logged in to restore prompts.',
          variant: 'destructive',
        });
        return null;
      }

      setRestoring(true);
      setError(null);
      try {
        const restored = await lessonPromptService.restoreFromHistory(historyId, user.id);
        toast({
          title: 'Prompt restored',
          description: 'Successfully restored prompt from history.',
        });
        // Refresh history after restore
        await fetchHistory();
        return restored;
      } catch (err) {
        console.error('[usePromptHistory] Error restoring:', err);
        setError(err as Error);
        toast({
          title: 'Error restoring prompt',
          description: (err as Error).message,
          variant: 'destructive',
        });
        return null;
      } finally {
        setRestoring(false);
      }
    },
    [user?.id, toast, fetchHistory]
  );

  return {
    history,
    loading,
    restoring,
    error,
    refetch: fetchHistory,
    restoreFromHistory,
  };
}

/**
 * Hook for bulk importing prompts
 */
export function usePromptImport() {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const importPrompts = useCallback(
    async (prompts: LessonPromptInput[]) => {
      if (!user?.id) {
        toast({
          title: 'Not authenticated',
          description: 'You must be logged in to import prompts.',
          variant: 'destructive',
        });
        return 0;
      }

      setImporting(true);
      setProgress(0);
      setError(null);

      try {
        const total = prompts.length;
        let imported = 0;

        for (let i = 0; i < prompts.length; i++) {
          try {
            await lessonPromptService.savePrompt(prompts[i], user.id);
            imported++;
          } catch (err) {
            console.warn(`[usePromptImport] Failed to import ${prompts[i].lessonId}:`, err);
          }
          setProgress(Math.round(((i + 1) / total) * 100));
        }

        toast({
          title: 'Import complete',
          description: `Successfully imported ${imported} of ${total} prompts.`,
        });

        return imported;
      } catch (err) {
        console.error('[usePromptImport] Error:', err);
        setError(err as Error);
        toast({
          title: 'Import failed',
          description: (err as Error).message,
          variant: 'destructive',
        });
        return 0;
      } finally {
        setImporting(false);
      }
    },
    [user?.id, toast]
  );

  return { importing, progress, error, importPrompts };
}
